import type { JSX } from 'react'

/**
 * SEO helpers shared by every public route's `head()`. Pure and
 * dependency-free (no server-only imports), so it's safe to use from route
 * files that run isomorphically.
 *
 * `SITE_URL` is hardcoded rather than derived from `BETTER_AUTH_URL` (which is
 * the app's own base URL, and in dev is `http://localhost:3000` — not what we
 * want baked into canonical/og:url tags or the sitemap). Kept as a single
 * constant for simplicity; revisit with a `SITE_URL` env var if we ever need
 * per-environment sitemaps (e.g. a staging deploy that shouldn't advertise
 * masklabs.io URLs).
 */
export const SITE_URL = 'https://masklabs.io'
export const SITE_NAME = 'Masklabs'

export const DEFAULT_DESCRIPTION =
  'Real US mobile carrier IPs on HTTP or SOCKS5. Monthly plans from $30, prepaid data you refill anytime, and no surprise overages.'

/** Default social-share card (1200x630, `public/og-image.png`). Absolute URLs
 *  are built from `SITE_URL` in `pageMeta`; the root sets the same default so
 *  even a page without `pageMeta` shares a real image. */
export const OG_IMAGE_PATH = '/og-image.png'
export const OG_IMAGE_ALT = 'Masklabs'

export interface PageSeoOptions {
  /** Full `<title>` text, including the "· Masklabs" suffix. */
  title: string
  description: string
  /** Path starting with `/`, e.g. `/pricing` — combined with `SITE_URL`. */
  path: string
  type?: 'website' | 'article'
  /** ISO date/time — set for blog posts, becomes `article:published_time`. */
  publishedTime?: string
  /** Set for pages that shouldn't be indexed (auth forms, etc). */
  noindex?: boolean
  /** Share-image path (default `OG_IMAGE_PATH`), combined with `SITE_URL`. */
  image?: string
  imageAlt?: string
}

/** Builds the meta tag array for a route's `head()` — title/description plus
 *  Open Graph and Twitter card tags, including the share image. */
export function pageMeta({
  title,
  description,
  path,
  type = 'website',
  publishedTime,
  noindex,
  image = OG_IMAGE_PATH,
  imageAlt = OG_IMAGE_ALT,
}: PageSeoOptions): Array<JSX.IntrinsicElements['meta']> {
  const url = `${SITE_URL}${path}`
  const imageUrl = `${SITE_URL}${image}`
  const meta: Array<JSX.IntrinsicElements['meta']> = [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:type', content: type },
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: imageAlt },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: imageAlt },
  ]
  if (type === 'article' && publishedTime) {
    meta.push({ property: 'article:published_time', content: publishedTime })
  }
  if (noindex) {
    meta.push({ name: 'robots', content: 'noindex, nofollow' })
  }
  return meta
}

/** Builds the canonical `<link>` for a route's `head()`. */
export function pageLinks(path: string): Array<JSX.IntrinsicElements['link']> {
  return [{ rel: 'canonical', href: `${SITE_URL}${path}` }]
}
